import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useDeleteRevenue,
  useRevenues,
  useUpsertRevenue,
} from '../hooks/use-dossier-company';

export function ProfileRevenuesSection() {
  const { data, isLoading } = useRevenues();
  const upsert = useUpsertRevenue();
  const remove = useDeleteRevenue();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  function reset() {
    setYear(String(new Date().getFullYear() - 1));
    setAmount('');
    setNotes('');
  }

  async function handleSubmit() {
    const yearInt = parseInt(year, 10);
    const amountFloat = parseFloat(amount.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(yearInt) || !Number.isFinite(amountFloat)) return;
    await upsert.mutateAsync({
      year: yearInt,
      revenueEur: amountFloat,
      notes: notes || undefined,
    });
    reset();
    setDialogOpen(false);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          3 derniers exercices clos minimum (jusqu'à 5 ans d'historique).
        </p>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Ajouter / mettre à jour
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Année</TableHead>
              <TableHead>CA HT (EUR)</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                  Chargement...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (!data || data.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                  Aucun CA saisi.
                </TableCell>
              </TableRow>
            )}
            {data?.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.year}</TableCell>
                <TableCell>{formatEur(r.revenueEur)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.notes ?? ''}</TableCell>
                <TableCell>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => remove.mutate(r.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) reset(); setDialogOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Saisir un CA annuel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="year">Année</Label>
                <Input
                  id="year"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Montant (EUR)</Label>
                <Input
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="ex. 2 500 000"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="ex. Inclut filiale X"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={!year || !amount || upsert.isPending}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatEur(value: string): string {
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return value;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(num);
}
